import React, { useEffect, useState } from 'react';
import { Box, Button, makeStyles } from '@material-ui/core';
import { Add, History } from '@material-ui/icons';
import { AxiosError } from 'axios';
import { groupBy } from 'lodash';
import { PPR_API_URL } from 'api';
import { IAvailableColumnModel } from 'interfaces';
import DynamicTable from 'components/DynamicTable';
import useError from 'components/ErrorProvider/useError';
import { IEquipmentType } from 'pages/PageEquipmentTypePprList/interfaces';
import {
  archiveTechnicalRoom,
  getTechnicalRoomAvailableGroupColumns,
  createTechnicalRoom,
  getTechnicalRoomList,
  loadTechnicalRoom,
  updateTechnicalRoom,
  loadTechnicalRoomHistoryList,
} from './api';
import { ITechnicalRoom, ITechnicalRoomHistory } from './interfaces';
import { TechnicalRoomDialog, TechnicalRoomHistoryDialog } from './components';

const useStyles = makeStyles({
  root: {
    padding: 20,
    backgroundColor: '#fff',

    '& .dynamic-table': {
      '&-controls': {
        margin: '-36px 0 0',
      },
      '&-area': {
        height: 'calc(100vh - 610px)',
      },
    },
  },
});

interface IProps {
  columns: IAvailableColumnModel[];
  equipmentTypes: IEquipmentType[];
  realEstateId: string;
}

const View: React.FC<IProps> = ({ columns, equipmentTypes, realEstateId }) => {
  // Local data
  const [currentTechnicalRoom, setCurrentTechnicalRoom] = useState<ITechnicalRoom | null>(null);
  const [technicalRoomHistoryList, setTechnicalRoomHistoryList] = useState<ITechnicalRoomHistory[]>([]);
  const [technicalRoomDialogOpened, setOpenedTechnicalRoomDialog] = useState(false);
  const [technicalRoomHistoryDialogOpened, setOpenedTechnicalRoomHistoryDialog] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isForceUpdating, setIsForceUpdating] = useState(false);

  // Styles
  const classes = useStyles();

  // Global notification
  const { addError } = useError();

  // DynamicTable props
  const visibleColumns = columns.filter(
    (column) =>
      !['technicalRoomId', 'equipmentTypeIds', 'realEstateId', 'realEstateName', 'equipmentIds'].includes(
        column.columnName,
      ),
  );

  const visibleFilters = columns.filter(
    (column) =>
      !['technicalRoomId', 'equipmentTypeNames', 'realEstateId', 'realEstateName', 'equipmentNames'].includes(
        column.columnName,
      ),
  );

  // Methods
  const handleSubmitTechnicalRoom = async (technicalRoom: ITechnicalRoom) => {
    setDisabled(true);

    try {
      currentTechnicalRoom
        ? await updateTechnicalRoom(currentTechnicalRoom.technicalRoomId!, { ...technicalRoom, realEstateId })
        : await createTechnicalRoom({ ...technicalRoom, realEstateId });

      setOpenedTechnicalRoomDialog(false);
      setIsForceUpdating(true);
    } catch (error) {
      addError((error as AxiosError).response!.data.message);
    } finally {
      setDisabled(false);
    }
  };

  const handleClickTechnicalRoomRow = async (technicalRoom: ITechnicalRoom) => {
    if (!technicalRoom.technicalRoomId) return;
    handleOpenTechnicalRoomDialog();
    setLoading(true);

    try {
      const { equipments, ...props } = await loadTechnicalRoom(technicalRoom.technicalRoomId);

      setCurrentTechnicalRoom({
        ...props,
        equipments,
        modifiedEquipments: Object.entries(groupBy(equipments, 'equipmentTypeId')).map(([, equipments]) => {
          const [{ equipmentTypeId, equipmentTypeName }] = equipments;
          return { equipmentType: { equipmentTypeId, equipmentTypeName }, equipments };
        }),
      });
    } catch (error) {
      addError((error as AxiosError).response!.data.message);
    } finally {
      setLoading(false);
    }
  };

  const handleArchiveTechnicalRoom = (isDeleted: boolean) => async (technicalRoom: ITechnicalRoom) => {
    if (!technicalRoom.technicalRoomId) return;

    try {
      await archiveTechnicalRoom(technicalRoom.technicalRoomId, isDeleted);
    } catch (error) {
      addError((error as AxiosError).response!.data.message);
    } finally {
      setIsForceUpdating(true);
    }
  };

  const handleLoadTechnicalRoomHistory = async (technicalRoom: ITechnicalRoom) => {
    if (!technicalRoom.technicalRoomId) return;
    setOpenedTechnicalRoomHistoryDialog(true);
    setLoading(true);

    try {
      setTechnicalRoomHistoryList(await loadTechnicalRoomHistoryList(technicalRoom.technicalRoomId));
    } catch (error) {
      addError((error as AxiosError).response!.data.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenTechnicalRoomDialog = () => {
    setOpenedTechnicalRoomDialog(true);
    setCurrentTechnicalRoom(null);
  };

  useEffect(() => setIsForceUpdating(true), [realEstateId]);

  return (
    <>
      <Box className={classes.root}>
        <Button variant="outlined" startIcon={<Add />} onClick={handleOpenTechnicalRoomDialog}>
          Добавить
        </Button>

        <DynamicTable
          pageNo={1}
          pageSize={20}
          getItems={getTechnicalRoomList(realEstateId)}
          setItemLink={() => ''}
          requiredFields={['technicalRoomId', 'realEstateId', 'equipmentNames']}
          visibleColumns={visibleColumns}
          visibleFilters={visibleFilters}
          hideColumns={['technicalRoomId', 'realEstateId', 'realEstateName']}
          hideFilters={['realEstateId', 'equipmentNames']}
          getAvailableGroupColumns={getTechnicalRoomAvailableGroupColumns}
          onDelete={handleArchiveTechnicalRoom(true)}
          onRestore={handleArchiveTechnicalRoom(false)}
          onClick={handleClickTechnicalRoomRow}
          forceUpdating={isForceUpdating}
          stopForceUpdating={() => setIsForceUpdating(false)}
          canReset
          actions={(item: ITechnicalRoom) => [
            {
              icon: <History />,
              label: 'История изменений',
              action: () => handleLoadTechnicalRoomHistory(item),
              IconButtonProps: { disabled },
            },
          ]}
          baseApiUrl={PPR_API_URL}
          listCountUrl={`/dictionary/technicalRoom/listCount/${realEstateId}`}
        />
      </Box>

      <TechnicalRoomDialog
        open={technicalRoomDialogOpened}
        technicalRoom={currentTechnicalRoom}
        equipmentTypes={equipmentTypes}
        onSubmitTechnicalRoom={handleSubmitTechnicalRoom}
        onClose={() => setOpenedTechnicalRoomDialog(false)}
        disabled={disabled}
        loading={loading}
      />

      <TechnicalRoomHistoryDialog
        open={technicalRoomHistoryDialogOpened}
        technicalRoomHistoryList={technicalRoomHistoryList}
        onClose={() => setOpenedTechnicalRoomHistoryDialog(false)}
        disabled={disabled}
        loading={loading}
      />
    </>
  );
};

export default View;
